import ConversationRecord from "models/view-models/conversations/conversation-record";
import {
    ConversationStateStatus,
    CurrentConversationState,
} from "./current-conversation/conversation-atom.reducer";
import { createConversationMessageStore } from "./current-conversation/create-conversation-message-store";
import { ConversationHistoryState } from "./conversation-history/conversation-history-atom-reducer";
import LocalStorageKey from "utilities/enumerations/local-storage-keys";

interface ConversationsPersistenceState {
    modalConversation: CurrentConversationState;
    bookviewConversation: CurrentConversationState;
    conversationHistory: ConversationHistoryState;
}

/**
 * Retrieves the saved state of conversations from local storage.
 */
export function getSavedState<T extends keyof ConversationsPersistenceState>(
    key: T,
    defaultState: ConversationsPersistenceState[T]
): ConversationsPersistenceState[T] {
    const serializedState = localStorage.getItem(LocalStorageKey.Conversations);
    const fullState: Partial<ConversationsPersistenceState> = serializedState
        ? JSON.parse(serializedState)
        : {};

    const rawState = fullState?.[key];
    if (!rawState) return defaultState;

    if (key === "conversationHistory") {
        return {
            ...defaultState,
            ...rawState,
        };
    }

    const currentState = rawState as CurrentConversationState;
    const currentConversation =
        currentState.currentConversation != null
            ? new ConversationRecord(currentState.currentConversation)
            : undefined;

    const conversationMessageStore =
        currentConversation != null
            ? createConversationMessageStore().add(
                  ...(currentConversation.messages ?? [])
              )
            : undefined;

    return {
        ...defaultState,
        ...currentState,
        conversationMessageStore,
        status:
            currentState.status === ConversationStateStatus.ConversationReady ||
            currentState.status ===
                ConversationStateStatus.ConversationMaxLengthReached
                ? currentState.status
                : (defaultState as CurrentConversationState).status,
        currentConversation:
            currentConversation?.with({
                messages: conversationMessageStore?.build(),
            }) ??
            (defaultState as CurrentConversationState).currentConversation,
    };
}

/**
 * Saves the given slice of conversation state to local storage.
 */
export function saveState<T extends keyof ConversationsPersistenceState>(
    key: T,
    state: Partial<ConversationsPersistenceState[T]>
): void {
    const serializedState = localStorage.getItem(LocalStorageKey.Conversations);
    const fullState: Partial<ConversationsPersistenceState> = serializedState
        ? JSON.parse(serializedState)
        : {};

    if (key === "conversationHistory") {
        const historyState = state as Partial<ConversationHistoryState>;
        fullState[key] = {
            conversations: historyState.conversations ?? [],
        } as ConversationsPersistenceState[T];
    } else {
        const convState = state as Partial<CurrentConversationState>;

        fullState[key] = {
            currentConversation: convState.currentConversation,
            status: getStateStatus(convState),
        } as ConversationsPersistenceState[T];
    }

    localStorage.setItem(
        LocalStorageKey.Conversations,
        JSON.stringify(fullState)
    );
}

function getStateStatus(
    state: Partial<CurrentConversationState>
): ConversationStateStatus {
    if (state.currentConversation == null || state.status == null) {
        return ConversationStateStatus.Uninitialized;
    }

    if (
        state.status === ConversationStateStatus.Uninitialized ||
        state.status === ConversationStateStatus.ConversationMaxLengthReached ||
        state.status === ConversationStateStatus.ConversationReady
    ) {
        return state.status;
    }

    return ConversationStateStatus.ConversationReady;
}
